"use client";

import posthog from "posthog-js";
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@/contexts/user-provider";
import { defaultLayout, resolveSections } from "@/lib/overview-section-registry";
import { OverviewSection, OverviewSectionPreference } from "@/types/overview-layout";
import { useUpdateOverviewLayoutMutation } from "@/query/settings/preferences";
type Mode = "read" | "edit";
interface OverviewLayoutState {
  saved: OverviewSectionPreference[];
  draft: OverviewSectionPreference[];
  mode: Mode;
}
interface OverviewLayoutActions {
  enterEditMode: () => void;
  cancelEdit: () => void;
  reorder: (orderedSections: OverviewSection[]) => void;
  hide: (section: OverviewSection) => void;
  show: (section: OverviewSection) => void;
  resetToDefaults: () => void;
  save: () => Promise<void>;
}
interface OverviewLayoutMeta {
  hasChanges: boolean;
  isSaving: boolean;
}
interface OverviewLayoutContextValue {
  state: OverviewLayoutState;
  actions: OverviewLayoutActions;
  meta: OverviewLayoutMeta;
}
const OverviewLayoutContext = createContext<OverviewLayoutContextValue | null>(null);
function isSameLayout(a: OverviewSectionPreference[], b: OverviewSectionPreference[]): boolean {
  if (a.length !== b.length) return false;
  const bySection = new Map(b.map(p => [p.section, p]));
  for (const pref of a) {
    const other = bySection.get(pref.section);
    if (!other) return false;
    if (other.position !== pref.position || other.visible !== pref.visible) {
      return false;
    }
  }
  return true;
}
function reindexPositions(prefs: OverviewSectionPreference[]): OverviewSectionPreference[] {
  // Visible sections keep their relative order at positions 0..n-1; hidden
  // sections trail after, also dense. This keeps positions tidy after every
  // mutation so the server never sees gaps.
  const visible = prefs.filter(p => p.visible).sort((a, b) => a.position - b.position);
  const hidden = prefs.filter(p => !p.visible).sort((a, b) => a.position - b.position);
  return [...visible, ...hidden].map((p, i) => ({
    ...p,
    position: i
  }));
}
export function OverviewLayoutProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    currentUser,
    setCurrentUser
  } = useUser();
  const updateMutation = useUpdateOverviewLayoutMutation();
  const saved = useMemo(() => reindexPositions(resolveSections(currentUser?.overviewLayout ?? defaultLayout())), [currentUser?.overviewLayout]);
  const [draft, setDraft] = useState<OverviewSectionPreference[]>(saved);
  const [mode, setMode] = useState<Mode>("read");
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Re-sync draft to saved whenever the server value changes — but only when
  // the user isn't actively editing, so we don't clobber in-flight changes.
  useEffect(() => {
    if (modeRef.current === "read") {
      setDraft(saved);
    }
  }, [saved]);

  // If the provider unmounts mid-edit (route change, page close), treat it as
  // abandonment so the analytics event fires even when neither Cancel nor Save
  // ran. Empty deps — cleanup runs only on unmount.
  useEffect(() => {
    return () => {
      if (modeRef.current === "edit") {
        posthog.capture("patient_overview_edit_exited", {
          saved_changes: false
        });
      }
    };
  }, []);
  const enterEditMode = useCallback(() => {
    setDraft(saved);
    setMode("edit");
  }, [saved]);
  const cancelEdit = useCallback(() => {
    setDraft(saved);
    setMode("read");
    posthog.capture("patient_overview_edit_exited", {
      saved_changes: false
    });
  }, [saved]);
  const reorder = useCallback((orderedSections: OverviewSection[]) => {
    setDraft(prev => {
      const bySection = new Map(prev.map(p => [p.section, p]));
      const visible = orderedSections.map((section, i) => {
        const existing = bySection.get(section);
        return existing ? {
          ...existing,
          position: i,
          visible: true
        } : {
          section,
          position: i,
          visible: true
        };
      });
      const hidden = prev.filter(p_0 => !orderedSections.includes(p_0.section)).sort((a, b) => a.position - b.position).map((p_1, i_0) => ({
        ...p_1,
        position: orderedSections.length + i_0
      }));
      return [...visible, ...hidden];
    });
  }, []);
  const hide = useCallback((section_0: OverviewSection) => {
    setDraft(prev_0 => reindexPositions(prev_0.map(p_2 => p_2.section === section_0 ? {
      ...p_2,
      visible: false
    } : p_2)));
  }, []);
  const show = useCallback((section_1: OverviewSection) => {
    setDraft(prev_1 => {
      const pref = prev_1.find(p_3 => p_3.section === section_1);
      if (!pref) return prev_1;
      const rest = prev_1.filter(p_4 => p_4.section !== section_1);
      const visibleRest = rest.filter(p_5 => p_5.visible).sort((a_0, b_0) => a_0.position - b_0.position);
      const hiddenRest = rest.filter(p_6 => !p_6.visible).sort((a_1, b_1) => a_1.position - b_1.position);
      const ordered = [{
        ...pref,
        visible: true
      }, ...visibleRest, ...hiddenRest];
      return ordered.map((p_7, i_1) => ({
        ...p_7,
        position: i_1
      }));
    });
  }, []);
  const resetToDefaults = useCallback(() => {
    setDraft(defaultLayout());
  }, []);
  const save = useCallback(async () => {
    const payload = draft;
    const result = await updateMutation.mutateAsync(payload);
    if (result && currentUser) {
      setCurrentUser({
        ...currentUser,
        overviewLayout: result.sections
      });
    }
    setMode("read");
    posthog.capture("patient_overview_edit_exited", {
      saved_changes: true
    });
  }, [draft, updateMutation, currentUser, setCurrentUser]);
  const hasChanges = useMemo(() => !isSameLayout(draft, saved), [draft, saved]);
  const value = useMemo<OverviewLayoutContextValue>(() => ({
    state: {
      saved,
      draft,
      mode
    },
    actions: {
      enterEditMode,
      cancelEdit,
      reorder,
      hide,
      show,
      resetToDefaults,
      save
    },
    meta: {
      hasChanges,
      isSaving: updateMutation.isPending
    }
  }), [saved, draft, mode, enterEditMode, cancelEdit, reorder, hide, show, resetToDefaults, save, hasChanges, updateMutation.isPending]);
  return <OverviewLayoutContext.Provider value={value}>
      {children}
    </OverviewLayoutContext.Provider>;
}
export function useOverviewLayout() {
  const ctx = useContext(OverviewLayoutContext);
  if (!ctx) {
    throw new Error("useOverviewLayout must be used within an OverviewLayoutProvider");
  }
  return ctx;
}
export function useOverviewLayoutOptional() {
  return useContext(OverviewLayoutContext);
}